import { LeftSidebarDirectory } from "../../types";
import { DirectoryState } from "./types";

/**
 * Core logic to determine resize capability for a directory.
 *
 * @param directory - The directory to check resize capability for
 * @param directories - Record of all directory states
 * @returns Object with:
 *   - canResize: Whether resize is possible
 *   - effectiveDirectory: The directory that will actually resize (may differ for collapsed dirs with pass-through)
 *   - previousDirectory: The previous directory in the sequence
 */
export function getResizeInfo(
  directory: LeftSidebarDirectory,
  directories: Record<LeftSidebarDirectory, DirectoryState>
): {
  canResize: boolean;
  effectiveDirectory: LeftSidebarDirectory | null;
  previousDirectory: LeftSidebarDirectory | null;
} {
  const directoryNames = Object.keys(directories) as LeftSidebarDirectory[];
  const directoryIndex = directoryNames.indexOf(directory);

  // Rule 1: First directory can never resize
  if (directoryIndex === 0 || directoryIndex === -1) {
    return {
      canResize: false,
      effectiveDirectory: null,
      previousDirectory: null,
    };
  }

  const previousDirectory = directoryNames[directoryIndex - 1];
  const currentState = directories[directory];
  const previousState = directories[previousDirectory];

  // Rule 2: Previous directory must be open (can't resize against collapsed)
  if (!previousState.isOpen) {
    return { canResize: false, effectiveDirectory: null, previousDirectory };
  }

  // Rule 3: EXPANDED directories - can resize if previous is open (already checked above)
  if (currentState.isOpen) {
    return {
      canResize: true,
      effectiveDirectory: directory,
      previousDirectory,
    };
  }

  // Rule 4: COLLAPSED directories - pass-through logic
  // Find the first open directory after this collapsed one
  let effectiveDirectory: LeftSidebarDirectory | null = null;
  for (let i = directoryIndex + 1; i < directoryNames.length; i++) {
    const nextDir = directoryNames[i];
    if (directories[nextDir].isOpen) {
      effectiveDirectory = nextDir;
      break;
    }
  }

  // Can only resize if we found an open directory to pass-through to
  return {
    canResize: effectiveDirectory !== null,
    effectiveDirectory,
    previousDirectory,
  };
}
